import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import { computed, observable } from 'mobx'
import type { BookkeepingStage } from './bookkeeping-stage'
import { once } from 'lodash-es'
import type { BookkeepingAccessStatus } from './bookkeeping-access-status'
import { getDisplayStage } from './bookkeeping-display-stage'

/**
 * Props for {@link BookkeepingProgressionInfo}.
 */
export type BookkeepingProgressionInfoProps =
  ModelProps<BookkeepingProgressionInfo>

export class BookkeepingProgressionInfo extends Model<BookkeepingProgressionInfo> {
  @observable
  accessor id = Id.none

  @observable
  accessor stage: BookkeepingStage = 'Unspecified'

  @observable
  accessor cycles: BookkeepingCycle[] = []

  @observable
  accessor workItems: WorkItem[] = []

  @observable
  accessor accessStatus: BookkeepingAccessStatus = 'NotRequested'

  @observable
  accessor platformConnection: ProvidedPlatformConnection | null = null

  /**
   * The stage.
   */
  @computed
  get displayStage() {
    return getDisplayStage({
      stage: this.stage,
      accessStatus: this.accessStatus,
      cycles: this.cycles,
    })
  }

  /**
   * The bookkeeping completed date.
   */
  @computed
  get completedAt() {
    const lastCompleted = this.cycles
      .map((c) => c.status)
      .filter((s) => s.type === 'Completed')
      .at(-1)

    return lastCompleted?.completedAt
  }

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() => new BookkeepingProgressionInfo())

/**
 * Work item completion.
 */
export interface WorkItemCompletion {
  id: Id
  completedCount: number
  name: string
  description: string
}

/**
 * Work item.
 */
export interface WorkItem {
  workItemId: Id
  inventoryItemId: Id
  inventoryItemName: string
  inventoryItemDescription: string
  quantity: number
  completedCount: number
}

/**
 * Bookkeeping cycle.
 */
export interface BookkeepingCycle {
  id: Id
  number: number
  startedAt: Date
  status: BookkeepingCycleStatus
}

/**
 * The bookkeeping cycle status.
 */
export type BookkeepingCycleStatus =
  | BookkeepingCycleInProgress
  | BookkeepingCycleCompleted
  | BookkeepingCycleCanceled
  | BookkeepingCycleUnknown

interface BookkeepingCycleInProgress {
  type: 'InProgress'
}

interface BookkeepingCycleCompleted {
  type: 'Completed'
  completedAt: Date
  completedById: Id
  items: WorkItemCompletion[]
}

interface BookkeepingCycleCanceled {
  type: 'Canceled'
  canceledAt: Date
}

interface BookkeepingCycleUnknown {
  type: 'Unknown'
}

/**
 * The bookkeeping platform connection.
 */
export type ProvidedPlatformConnection =
  | QuickBooksPlatformConnection
  | XeroPlatformConnection
  | OtherPlatformConnection

interface QuickBooksPlatformConnection {
  type: 'QuickBooks'
  quickBooks: {
    connectionId: Id
    realmId: Id
    companyName: string
  }
}

interface XeroPlatformConnection {
  type: 'Xero'
  xero: {
    connectionId: Id
    tenantId: Id
    tenantName: string
  }
}

interface OtherPlatformConnection {
  type: 'Other'
  other: {
    info: string
  }
}
